import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, Package, Truck, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import SquarePaymentForm from '../components/SquarePaymentForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  // Coupon code state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  
  // State for selections
  const [hardwoodColor, setHardwoodColor] = useState('');
  const [hardwoodSqft, setHardwoodSqft] = useState('1500');
  const [lvpColor, setLvpColor] = useState('');
  const [lvpSqft, setLvpSqft] = useState('1500');
  
  // State for immediate shipping calculation
  const [showImmediateShipping, setShowImmediateShipping] = useState({ hardwood: false, lvp: false });
  
  // State for container assignment preview
  const [containerAssignment, setContainerAssignment] = useState({ hardwood: null, lvp: null });
  
  // Sample box modal state
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [sampleProductName, setSampleProductName] = useState('');
  const [sampleProductId, setSampleProductId] = useState('');
  const [sampleStep, setSampleStep] = useState('info'); // 'info', 'shipping', 'otp', 'payment'
  const [sampleShipping, setSampleShipping] = useState({ name: '', street: '', city: '', state: '', zip_code: '' });
  const [sampleEmail, setSampleEmail] = useState('');
  const [sampleOtpSent, setSampleOtpSent] = useState(false);
  const [sampleOtpCode, setSampleOtpCode] = useState('');
  const [sampleOtpVerified, setSampleOtpVerified] = useState(false);
  const [sampleVerificationToken, setSampleVerificationToken] = useState('');
  const [sampleOrderId, setSampleOrderId] = useState('');
  const [sampleProcessing, setSampleProcessing] = useState(false);
  
  // Helper function to get image for a product - now uses real product images from database
  const getProductImage = (product) => {
    if (!product) return null;
    
    // Use the image_url from the product if available
    if (product.image_url) {
      return product.image_url;
    }
    
    // Fallback to placeholder if no image
    return 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, userRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/products`),
          (() => {
            const token = localStorage.getItem('bluecrate_session_token');
            const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
            return axios.get(`${BACKEND_URL}/api/me`, config).catch(() => null);
          })()
        ]);
        setProducts(productsRes.data);
        if (userRes?.data) {
          setUser(userRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Check container assignment when quantity changes
  useEffect(() => {
    const checkHardwoodContainer = async () => {
      const sqft = parseInt(hardwoodSqft);
      if (sqft >= 1500) {
        try {
          const response = await axios.post(`${BACKEND_URL}/api/containers/check-fit`, { sqft });
          setContainerAssignment(prev => ({ ...prev, hardwood: response.data }));
        } catch (error) {
          console.error('Error checking container fit:', error);
        }
      }
    };

    const debounce = setTimeout(checkHardwoodContainer, 500);
    return () => clearTimeout(debounce);
  }, [hardwoodSqft]);

  useEffect(() => {
    const checkLvpContainer = async () => {
      const sqft = parseInt(lvpSqft);
      if (sqft >= 1500) {
        try {
          const response = await axios.post(`${BACKEND_URL}/api/containers/check-fit`, { sqft });
          setContainerAssignment(prev => ({ ...prev, lvp: response.data }));
        } catch (error) {
          console.error('Error checking container fit:', error);
        }
      }
    };

    const debounce = setTimeout(checkLvpContainer, 500);
    return () => clearTimeout(debounce);
  }, [lvpSqft]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  // Group products by category
  const solidHardwood = products.filter(p => p.category === 'solid_hardwood');
  const engineeredHardwood = products.filter(p => p.category === 'engineered_hardwood');
  const lvpSpc = products.filter(p => p.category === 'spc');
  const allHardwood = [...solidHardwood, ...engineeredHardwood];

  // Calculate price based on membership or applied coupon (flat pricing, no volume tiers)
  const calculatePrice = (product, sqft) => {
    if (!product || !sqft) return 0;
    
    // Check if coupon is applied (PILOT2026 grants elite/member pricing)
    if (appliedCoupon && appliedCoupon.membership_tier) {
      const couponTier = appliedCoupon.membership_tier.toLowerCase();
      // Map old tier names to new single 'member' tier
      if (couponTier === 'elite' || couponTier === 'member') {
        if (product.member_pricing?.member) return product.member_pricing.member;
        if (product.member_pricing?.elite) return product.member_pricing.elite;
      }
      if (product.member_pricing && product.member_pricing[couponTier]) {
        return product.member_pricing[couponTier];
      }
    }
    
    // Check membership level (new single-tier system)
    if (user && user.membership_level) {
      const memberLevel = user.membership_level.toLowerCase();
      // User is a member - give member pricing
      if (memberLevel === 'member' || memberLevel === 'elite' || memberLevel === 'pro' || memberLevel === 'basic') {
        if (product.member_pricing?.member) return product.member_pricing.member;
        if (product.member_pricing?.elite) return product.member_pricing.elite;
        if (product.member_pricing && product.member_pricing[memberLevel]) {
          return product.member_pricing[memberLevel];
        }
      }
    }
    
    // Check membership_tier field as well (from auth response)
    if (user && user.membership_tier && user.membership_tier !== 'none') {
      if (product.member_pricing?.member) return product.member_pricing.member;
      if (product.member_pricing?.elite) return product.member_pricing.elite;
    }
    
    // Return base retail price if not a member
    return product.base_price;
  };

  // Calculate immediate shipping cost (LCL - more expensive)
  const calculateImmediateShipping = (sqft) => {
    const quantity = parseInt(sqft);
    // LCL is more expensive - customer pays full proportional freight + premium
    // Shared container: ~$1600 for typical order
    // LCL direct: 2-3x more expensive due to no sharing
    const lclCostPerSqft = 1.50; // $1.50/sqft for direct LCL shipping
    const immediateCost = lclCostPerSqft * quantity;
    return immediateCost.toFixed(2);
  };


  // Apply coupon code
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/coupons/validate`, {
        code: couponCode.trim().toUpperCase()
      });
      
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        toast.success(`Coupon applied! You now have ${response.data.membership_tier} pricing`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid coupon code');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to apply coupon');
      }
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  // Snap sqft to nearest full box (round up)
  const snapToFullBox = (rawSqft, sqftPerBox) => {
    if (!sqftPerBox || !rawSqft) return parseInt(rawSqft) || 0;
    return Math.ceil(parseFloat(rawSqft) / sqftPerBox) * sqftPerBox;
  };

  const handlePledge = async (type) => {
    const color = type === 'hardwood' ? hardwoodColor : lvpColor;
    const rawSqft = type === 'hardwood' ? hardwoodSqft : lvpSqft;
    
    if (!color || !rawSqft || parseInt(rawSqft) < 1500) {
      toast.error('Please select a color and enter at least 1,500 sqft');
      return;
    }

    const selectedProduct = products.find(p => p.product_id === color);
    if (!selectedProduct) {
      toast.error('Product not found');
      return;
    }

    // Apply snap-to-full-box rounding
    const finalSqft = snapToFullBox(rawSqft, selectedProduct.sqft_per_box);

    // Navigate to checkout with pledge data (authentication handled in checkout page)
    const pledgeData = {
      product_id: selectedProduct.product_id,
      product_name: selectedProduct.name,
      sqft: finalSqft,
      price_per_sqft: calculatePrice(selectedProduct, finalSqft),
      total_price: (calculatePrice(selectedProduct, finalSqft) * finalSqft).toFixed(2)
    };

    navigate('/checkout', { 
      state: { 
        pledgeData 
      } 
    });
  };

  const handleShipNow = async (type) => {
    const color = type === 'hardwood' ? hardwoodColor : lvpColor;
    const rawSqft = type === 'hardwood' ? hardwoodSqft : lvpSqft;
    
    if (!color || !rawSqft || parseInt(rawSqft) < 1500) {
      toast.error('Please select a color and enter at least 1,500 sqft');
      return;
    }

    const selectedProduct = products.find(p => p.product_id === color);
    if (!selectedProduct) {
      toast.error('Product not found');
      return;
    }

    // Apply snap-to-full-box rounding
    const finalSqft = snapToFullBox(rawSqft, selectedProduct.sqft_per_box);

    // Navigate directly to Ship Now page with product data
    const pledgeData = {
      product_id: selectedProduct.product_id,
      product_name: selectedProduct.name,
      sqft: finalSqft,
      price_per_sqft: calculatePrice(selectedProduct, finalSqft),
      total_price: (calculatePrice(selectedProduct, finalSqft) * finalSqft).toFixed(2)
    };

    // Pass a flag to indicate we need to calculate shipping on ShipNow page
    navigate('/ship-now', { 
      state: { 
        pledgeData,
        calculateShippingOnLoad: true
      } 
    });
  };

  const handleOrderSample = (type) => {
    const color = type === 'hardwood' ? hardwoodColor : lvpColor;
    
    if (!color) {
      toast.error('Please select a color first');
      return;
    }

    const selectedProduct = products.find(p => p.product_id === color);
    if (selectedProduct) {
      setSampleProductName(selectedProduct.name);
      setSampleProductId(selectedProduct.product_id);
      setShowSampleModal(true);
    }
  };

  const renderProductOption = (type, title, icon, description, colorOptions, selectedColor, setSelectedColor, sqft, setSqft, basePrice) => {
    const selectedProduct = products.find(p => p.product_id === selectedColor);
    const sqftPerBox = selectedProduct?.sqft_per_box || null;
    const rawSqftNum = parseFloat(sqft) || 0;
    const effectiveSqft = sqftPerBox && rawSqftNum > 0
      ? Math.ceil(rawSqftNum / sqftPerBox) * sqftPerBox
      : rawSqftNum;
    const boxCount = sqftPerBox && effectiveSqft > 0 ? Math.round(effectiveSqft / sqftPerBox) : null;
    const wasAdjusted = sqftPerBox && rawSqftNum > 0 && effectiveSqft !== rawSqftNum;

    const pricePerSqft = selectedProduct ? calculatePrice(selectedProduct, effectiveSqft) : basePrice;
    const totalCost = (pricePerSqft * effectiveSqft).toFixed(2);
    const immediateShippingCost = calculateImmediateShipping(effectiveSqft.toString());
    const isMember = user && user.membership_level && user.membership_level !== 'none';

    return (
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100 hover:border-primary transition-all">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
            <span className="text-3xl">{icon}</span>
          </div>
          <h2 className="font-bold text-2xl mb-2">{title}</h2>
          <p className="text-sm text-gray-600">{description}</p>
          {isMember && (
            <div className="mt-2 inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
              {user.membership_level.toUpperCase()} MEMBER PRICING
            </div>
          )}
        </div>

        {/* Color Dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Select Color:</label>
          <select
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
          >
            <option value="">Choose a finish...</option>
            {colorOptions.map((product) => (
              <option key={product.product_id} value={product.product_id}>
                {product.name}
              </option>
            ))}
          </select>
        </div>

        {/* Product Image Display */}
        {selectedColor && (() => {
          const product = colorOptions.find(p => p.product_id === selectedColor);
          const imageUrl = getProductImage(product);
          return imageUrl ? (
            <>
              <div className="mb-4">
                <div className="relative rounded-xl overflow-hidden border-2 border-gray-200 shadow-md">
                  <img 
                    src={imageUrl} 
                    alt={product?.name || 'Product'} 
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    <button
                      onClick={() => handleOrderSample(type)}
                      className="bg-white/95 hover:bg-white backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-gray-200 transition-all hover:scale-105"
                    >
                      <span className="text-sm font-semibold text-primary">Order Sample Box</span>
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <p className="text-white font-semibold text-sm">
                      {product?.name}
                    </p>
                    <p className="text-white/80 text-xs">Click "Order Sample Box" to see this in person</p>
                  </div>
                </div>
              </div>
              
              {/* Product Specifications */}
              {product?.specifications && (
                <div className="mb-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="font-semibold text-sm text-gray-900 mb-3">Product Specifications</h4>
                  <div className="space-y-2 text-xs">
                    {product.specifications.dimensions && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Dimensions:</span>
                        <span className="font-medium text-gray-900">{product.specifications.dimensions}</span>
                      </div>
                    )}
                    {product.specifications.wear_layer && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Wear Layer:</span>
                        <span className="font-medium text-gray-900">{product.specifications.wear_layer}</span>
                      </div>
                    )}
                    {product.specifications.core_material && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Core:</span>
                        <span className="font-medium text-gray-900">{product.specifications.core_material}</span>
                      </div>
                    )}
                    {product.specifications.underlayment && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Underlayment:</span>
                        <span className="font-medium text-gray-900">{product.specifications.underlayment}</span>
                      </div>
                    )}
                    {product.specifications.coating && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Coating:</span>
                        <span className="font-medium text-gray-900">{product.specifications.coating}</span>
                      </div>
                    )}
                    {product.specifications.waterproof && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Waterproof:</span>
                        <span className="font-medium text-green-600">{product.specifications.waterproof}</span>
                      </div>
                    )}
                    {product.specifications.installation && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Installation:</span>
                        <span className="font-medium text-gray-900">{product.specifications.installation}</span>
                      </div>
                    )}
                    {product.specifications.warranty && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Warranty:</span>
                        <span className="font-medium text-gray-900">{product.specifications.warranty}</span>
                      </div>
                    )}
                  </div>
                  {product.manufacturer_code && (
                    <div className="mt-3 pt-3 border-t border-gray-300">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Product Code:</span>
                        <span className="text-xs font-mono font-semibold text-primary">{product.manufacturer_code}</span>
                      </div>
                      {product.availability?.status === 'in_stock' && (
                        <div className="mt-1 flex items-center justify-end">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                            In Stock - {product.availability.quantity_sqft?.toLocaleString()} sqft
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null;
        })()}

        {/* Square Footage Input */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Square Footage:</label>
          <input
            type="number"
            min="1500"
            step={sqftPerBox || 100}
            value={sqft}
            onChange={(e) => setSqft(e.target.value)}
            onBlur={(e) => {
              const raw = parseFloat(e.target.value) || 0;
              if (sqftPerBox && raw >= 1) {
                const rounded = Math.ceil(raw / sqftPerBox) * sqftPerBox;
                setSqft(rounded.toString());
              }
            }}
            className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
            placeholder="Enter sqft (min. 1,500)"
            data-testid={`sqft-input-${type}`}
          />
          {sqftPerBox ? (
            <div className="mt-2 space-y-1">
              {rawSqftNum >= 1 && (
                <div className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-lg ${wasAdjusted ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-blue-50 text-blue-700'}`} data-testid={`box-count-info-${type}`}>
                  {wasAdjusted
                    ? `Adjusted to ${effectiveSqft.toLocaleString()} sqft (${boxCount} full boxes × ${sqftPerBox} sqft/box)`
                    : `${boxCount} full boxes × ${sqftPerBox} sqft/box = ${effectiveSqft.toLocaleString()} sqft`
                  }
                </div>
              )}
              <p className="text-xs text-gray-500">Sold in full boxes of {sqftPerBox} sqft. Min 1,500 sqft</p>
            </div>
          ) : (
            <p className="text-xs text-gray-500 mt-1">Minimum: 1,500 sqft</p>
          )}
        </div>

        {/* Container Assignment Preview */}
        {selectedColor && effectiveSqft >= 1500 && containerAssignment[type] && (
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 mb-4 border-2 border-purple-200">
            <div className="flex items-center mb-2">
              <Package className="w-5 h-5 text-purple-600 mr-2" />
              <span className="font-bold text-sm text-purple-900">Container Status</span>
            </div>
            {containerAssignment[type].container_id ? (
              <div>
                <div className="bg-white rounded-xl p-3 border border-purple-200">
                  <p className="text-lg font-bold text-purple-700">
                    Container #{containerAssignment[type].container_number}
                  </p>
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Capacity</span>
                      <span>{containerAssignment[type].current_fill?.toLocaleString()} / {containerAssignment[type].total_capacity?.toLocaleString()} sqft</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${containerAssignment[type].fill_percentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {containerAssignment[type].available_space?.toLocaleString()} sqft available
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="bg-white rounded-xl p-3 border border-purple-200">
                  <p className="text-lg font-bold text-purple-700">
                    New Container
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Your pledge will create the first container!
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Capacity: {containerAssignment[type].total_capacity?.toLocaleString()} sqft
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Price Calculation */}
        {selectedColor && effectiveSqft >= 1500 && (
          <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl">
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-sm text-gray-700">Price per sqft:</span>
              <span className="font-bold text-2xl text-primary">${pricePerSqft.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-sm font-semibold text-gray-700">Total Cost:</span>
              <span className="font-bold text-3xl text-primary">${parseFloat(totalCost).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            {sqftPerBox && (
              <p className="text-xs text-blue-600 mb-2">{effectiveSqft.toLocaleString()} sqft ({boxCount} boxes)</p>
            )}
            <div className="pt-3 border-t border-blue-200">
              <p className="text-xs text-gray-600">
                <Check className="w-3 h-3 inline mr-1" />
                30% deposit to secure your order
              </p>
              <p className="text-xs text-gray-600">
                <Check className="w-3 h-3 inline mr-1" />
                70% due once ready to ship
              </p>
            </div>
          </div>
        )}

        {/* Immediate Shipping Option */}
        {selectedColor && effectiveSqft >= 1500 && (
          <div className="mb-6">
            <button
              onClick={() => setShowImmediateShipping({ ...showImmediateShipping, [type]: !showImmediateShipping[type] })}
              className="w-full p-4 border-2 border-orange-200 bg-orange-50 rounded-2xl text-left hover:border-orange-400 transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Truck className="w-5 h-5 text-orange-600 mr-2" />
                  <span className="font-semibold text-sm">Ready to ship right away?</span>
                </div>
                <ArrowRight className={`w-4 h-4 text-orange-600 transition-transform ${showImmediateShipping[type] ? 'rotate-90' : ''}`} />
              </div>
              {showImmediateShipping[type] && (
                <div className="mt-3 pt-3 border-t border-orange-200">
                  <p className="text-xs text-gray-600 mb-3">
                    Skip the wait! Direct shipping is faster but costs more.
                  </p>
                  
                  {/* Comparison Box */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Shared Container (FCL) - Cheaper */}
                    <div className="bg-green-50 border-2 border-green-300 rounded-xl p-3">
                      <div className="flex items-center mb-1">
                        <Package className="w-4 h-4 text-green-700 mr-1" />
                        <p className="text-xs font-bold text-green-900">Shared (Pledge)</p>
                      </div>
                      <p className="text-lg font-bold text-green-700">~$1,600</p>
                      <p className="text-xs text-green-600">Est. shipping</p>
                      <p className="text-xs text-green-700 mt-1">45 days</p>
                    </div>
                    
                    {/* Direct Ship (LCL) - Faster */}
                    <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-3">
                      <div className="flex items-center mb-1">
                        <Truck className="w-4 h-4 text-orange-700 mr-1" />
                        <p className="text-xs font-bold text-orange-900">Direct Ship</p>
                      </div>
                      <p className="text-lg font-bold text-orange-700">~${immediateShippingCost}</p>
                      <p className="text-xs text-orange-600">Est. shipping</p>
                      <p className="text-xs text-orange-700 mt-1">30 days</p>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-2 mb-2">
                    <p className="text-xs font-semibold text-yellow-900">
                      💡 Save ~${(parseFloat(immediateShippingCost) - 1600).toFixed(0)} with Shared Container!
                    </p>
                    <p className="text-xs text-yellow-800">Enter your ZIP at checkout for exact shipping costs</p>
                  </div>
                </div>
              )}
            </button>
          </div>
        )}

        {/* Membership Pricing Info */}
        {!isMember && selectedProduct && (
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-700 mb-2">Become a Member & Save 30%:</p>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-600">Your Price:</span>
                <span className="font-semibold text-gray-900">${basePrice.toFixed(2)}/sqft</span>
              </div>
              <div className="flex items-center justify-between text-primary">
                <span className="flex items-center font-semibold">
                  <Star className="w-4 h-4 mr-2 text-amber-500" />
                  Member Price:
                </span>
                <span className="font-bold text-lg">${selectedProduct.member_pricing?.member?.toFixed(2) || selectedProduct.member_pricing?.elite?.toFixed(2) || (basePrice * 0.7).toFixed(2)}/sqft</span>
              </div>
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-xs text-amber-800">
                  Join for <strong>$50/month</strong> (billed annually) and save on every order!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 gap-3">
          {/* Pledge Button (FCL - 30% Deposit) */}
          <Button
            onClick={() => handlePledge(type)}
            disabled={!selectedColor || effectiveSqft < 1500}
            className="w-full rounded-full h-12 text-lg bg-primary hover:bg-primary/90 disabled:opacity-50"
            data-testid={`pledge-btn-${type}`}
          >
            <Package className="w-5 h-5 mr-2" />
            Pledge {effectiveSqft >= 1500 ? effectiveSqft.toLocaleString() : ''} sqft
          </Button>

          {/* Ship Now Button (LCL - Full Payment) */}
          <Button
            onClick={() => handleShipNow(type)}
            disabled={!selectedColor || effectiveSqft < 1500}
            className="w-full rounded-full h-12 text-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
            data-testid={`ship-now-btn-${type}`}
          >
            <Truck className="w-5 h-5 mr-2" />
            Ship Now (Full Payment)
          </Button>
          
          <p className="text-xs text-center text-gray-500 mt-1">
            Pledge = 30% deposit, 70% when ready to ship | Ship Now = 100% for immediate production
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="products-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                Premium Materials
              </span>
            </div>
            <h1 className="font-bold text-4xl md:text-6xl tracking-tight leading-tight mb-4" data-testid="products-title">
              Select Your Flooring
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-testid="products-description">
              Premium engineered hardwood and LVP/SPC flooring with member pricing discounts
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Minimum order: 1,500 sqft
            </p>
          </div>

          {/* Coupon Code Section */}
          <div className="mb-12 max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">Have a Coupon Code?</h3>
                  <p className="text-sm text-gray-600">Get exclusive member pricing</p>
                </div>
                {appliedCoupon && (
                  <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {appliedCoupon.membership_tier.toUpperCase()} Active
                  </div>
                )}
              </div>
              
              {!appliedCoupon ? (
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && applyCoupon()}
                    placeholder="Enter code (e.g., PILOT2026)"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    disabled={couponLoading}
                  />
                  <Button
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    className="px-8 py-3 bg-primary hover:bg-primary/90"
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-white rounded-xl p-4">
                  <div>
                    <p className="font-semibold text-gray-900">Code: {appliedCoupon.code}</p>
                    <p className="text-sm text-gray-600">{appliedCoupon.description}</p>
                  </div>
                  <Button
                    onClick={removeCoupon}
                    variant="outline"
                    className="text-sm"
                  >
                    Remove
                  </Button>
                </div>
              )}
              
              {appliedCoupon && (
                <div className="mt-4 p-4 bg-white rounded-xl">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Your Pricing:</p>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    {appliedCoupon.membership_tier === 'elite' && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">LVP/SPC:</span>
                        <span className="font-bold text-green-600">$1.23/sqft</span>
                      </div>
                    )}
                    {appliedCoupon.membership_tier === 'pro' && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">LVP/SPC:</span>
                        <span className="font-bold text-green-600">$1.39/sqft</span>
                      </div>
                    )}
                    {appliedCoupon.membership_tier === 'basic' && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">LVP/SPC:</span>
                        <span className="font-bold text-green-600">$1.54/sqft</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Engineered Hardwood Option */}
            {renderProductOption(
              'hardwood',
              'Engineered Hardwood Flooring',
              '🌳',
              'Premium European Oak with superior finish',
              allHardwood,
              hardwoodColor,
              setHardwoodColor,
              hardwoodSqft,
              setHardwoodSqft,
              3.56  // Non-member price for Hardwood
            )}

            {/* LVP/SPC Option */}
            {renderProductOption(
              'lvp',
              'LVP/SPC Flooring',
              '💎',
              '20 mil wear layer with premium core - 100% waterproof',
              lvpSpc,
              lvpColor,
              setLvpColor,
              lvpSqft,
              setLvpSqft,
              2.24  // Non-member price for LVP
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Not a member?</strong> Join BlueCrate Membership for <strong>30% savings</strong> on every order.
            </p>
            {!user && (
              <p className="text-xs text-gray-500">
                Sign in to see your personalized member pricing
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Sample Box Order Modal */}
      {showSampleModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Package className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Order Sample Box</h3>
                  <p className="text-gray-600 text-sm">{sampleProductName}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowSampleModal(false);
                  setSampleStep('info');
                  setSampleOtpSent(false);
                  setSampleOtpCode('');
                  setSampleOtpVerified(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1: Info */}
            {sampleStep === 'info' && (
              <>
                <div className="bg-blue-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gray-700 mb-3"><strong>Sample Box Includes:</strong></p>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>6x9 inch flooring sample</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Product specification sheet</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Installation guide</span>
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Sample Box Cost:</span>
                    <span className="text-2xl font-bold text-primary">$15.00</span>
                  </div>
                  <p className="text-xs text-gray-500">Free shipping • Arrives in 3-5 business days</p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => setSampleStep('shipping')}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 rounded-full transition-all hover:scale-105"
                  >
                    Continue to Shipping
                  </button>
                  <button
                    onClick={() => setShowSampleModal(false)}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-full transition-all"
                  >
                    Maybe Later
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Shipping Info */}
            {sampleStep === 'shipping' && (
              <>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={sampleEmail}
                      onChange={(e) => setSampleEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={sampleShipping.name}
                      onChange={(e) => setSampleShipping(s => ({...s, name: e.target.value}))}
                      placeholder="John Doe"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={sampleShipping.street}
                      onChange={(e) => setSampleShipping(s => ({...s, street: e.target.value}))}
                      placeholder="123 Main St"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={sampleShipping.city}
                        onChange={(e) => setSampleShipping(s => ({...s, city: e.target.value}))}
                        placeholder="City"
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        value={sampleShipping.state}
                        onChange={(e) => setSampleShipping(s => ({...s, state: e.target.value}))}
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      >
                        <option value="">Select</option>
                        {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <input
                      type="text"
                      value={sampleShipping.zip_code}
                      onChange={(e) => setSampleShipping(s => ({...s, zip_code: e.target.value}))}
                      placeholder="12345"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      if (!sampleEmail || !sampleShipping.name || !sampleShipping.street || !sampleShipping.city || !sampleShipping.state || !sampleShipping.zip_code) {
                        toast.error('Please fill in all fields');
                        return;
                      }
                      setSampleStep('otp');
                    }}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 rounded-full"
                  >
                    Continue to Verification
                  </button>
                  <button
                    onClick={() => setSampleStep('info')}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-full"
                  >
                    Back
                  </button>
                </div>
              </>
            )}

            {/* Step 3: OTP Verification */}
            {sampleStep === 'otp' && !sampleOtpVerified && (
              <>
                <div className="bg-blue-50 rounded-xl p-4 mb-6">
                  <p className="text-sm font-medium text-blue-900 mb-2">Verify Your Email</p>
                  <p className="text-xs text-blue-700">
                    We'll send a 6-digit code to <strong>{sampleEmail}</strong>
                  </p>
                </div>

                {!sampleOtpSent ? (
                  <button
                    onClick={async () => {
                      setSampleProcessing(true);
                      try {
                        await axios.post(`${BACKEND_URL}/api/otp/send`, { email: sampleEmail.toLowerCase().trim() });
                        setSampleOtpSent(true);
                        toast.success('Verification code sent!');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to send code');
                      } finally {
                        setSampleProcessing(false);
                      }
                    }}
                    disabled={sampleProcessing}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 rounded-full mb-3"
                  >
                    {sampleProcessing ? 'Sending...' : 'Send Verification Code'}
                  </button>
                ) : (
                  <div className="space-y-4 mb-6">
                    <input
                      type="text"
                      maxLength={6}
                      value={sampleOtpCode}
                      onChange={(e) => setSampleOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="Enter 6-digit code"
                      className="w-full p-4 border-2 border-gray-200 rounded-xl text-center text-2xl tracking-widest font-mono"
                    />
                    <button
                      onClick={async () => {
                        if (sampleOtpCode.length !== 6) {
                          toast.error('Please enter the 6-digit code');
                          return;
                        }
                        setSampleProcessing(true);
                        try {
                          const res = await axios.post(`${BACKEND_URL}/api/otp/verify`, {
                            email: sampleEmail.toLowerCase().trim(),
                            otp_code: sampleOtpCode
                          });
                          setSampleVerificationToken(res.data.verification_token);
                          setSampleOtpVerified(true);
                          
                          // Create the sample order
                          const orderRes = await axios.post(`${BACKEND_URL}/api/sample-box/create-order`, {
                            email: sampleEmail.toLowerCase().trim(),
                            name: sampleShipping.name,
                            product_name: sampleProductName,
                            product_id: sampleProductId,
                            shipping_address: sampleShipping,
                            verification_token: res.data.verification_token
                          });
                          setSampleOrderId(orderRes.data.order_id);
                          setSampleStep('payment');
                          toast.success('Email verified!');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Verification failed');
                        } finally {
                          setSampleProcessing(false);
                        }
                      }}
                      disabled={sampleProcessing || sampleOtpCode.length !== 6}
                      className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 rounded-full"
                    >
                      {sampleProcessing ? 'Verifying...' : 'Verify & Continue'}
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setSampleStep('shipping')}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-full"
                >
                  Back
                </button>
              </>
            )}

            {/* Step 4: Payment */}
            {sampleStep === 'payment' && sampleOtpVerified && (
              <>
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Sample Box</span>
                    <span className="font-bold text-lg">$15.00</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Ships to: {sampleShipping.city}, {sampleShipping.state}</p>
                </div>

                <SquarePaymentForm
                  amount={15.00}
                  email={sampleEmail}
                  verificationToken={sampleVerificationToken}
                  orderData={{
                    order_id: sampleOrderId,
                    product_name: sampleProductName,
                    payment_type: 'sample_box'
                  }}
                  onSuccess={async (paymentData) => {
                    try {
                      await axios.post(`${BACKEND_URL}/api/sample-box/complete-payment`, {
                        order_id: sampleOrderId,
                        square_payment_id: paymentData.payment_id,
                        email: sampleEmail
                      });
                      toast.success('Sample box ordered successfully!');
                      setShowSampleModal(false);
                      // Reset state
                      setSampleStep('info');
                      setSampleShipping({ name: '', street: '', city: '', state: '', zip_code: '' });
                      setSampleEmail('');
                      setSampleOtpSent(false);
                      setSampleOtpCode('');
                      setSampleOtpVerified(false);
                      setSampleOrderId('');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to complete order');
                    }
                  }}
                  onError={(error) => toast.error(error)}
                  disabled={false}
                  forceDirectCharge={true}
                />
              </>
            )}

            <p className="text-xs text-center text-gray-500 mt-4">
              Questions? Email us at hello@bluecrategroup.com
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
